import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function FollowDigestCard() {
  const { user, reload } = useAuth();
  const [enabled, setEnabled] = useState(!user?.follow_digest_opt_out);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { setEnabled(!user?.follow_digest_opt_out); }, [user?.follow_digest_opt_out]);
  useEffect(() => { api.get("/portal/follow-digest/preview").then(({ data }) => setPreview(data)).catch(() => setPreview({ items: [] })); }, []);

  const toggle = async (v) => {
    setSaving(true);
    setEnabled(v);
    try {
      await api.patch("/auth/preferences", { follow_digest_enabled: v });
      toast.success(v ? "Weekly follow digest on" : "Weekly follow digest off");
      reload();
    } catch (e) { setEnabled(!v); toast.error(e?.response?.data?.detail || "Could not save preference"); }
    finally { setSaving(false); }
  };

  const n = preview?.items?.length || 0;
  return (
    <Card className="rounded-xl border bg-card p-6" data-testid="follow-digest-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <div>
            <h2 className="h-display text-lg font-semibold text-foreground">Weekly follow digest</h2>
            <p className="text-xs text-muted-foreground">One email a week summarising new updates and deck refreshes from founders you asked to keep you posted.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Switch checked={enabled} onCheckedChange={toggle} disabled={saving} aria-label="Weekly follow digest" data-testid="follow-digest-toggle" />
        </div>
      </div>
      <p className="mt-3 text-xs text-muted-foreground" data-testid="follow-digest-summary">
        {preview === null ? "Checking who you follow…" : n === 0
          ? "You're not following any decks yet. Tap “keep me posted” on a founder's deck and they'll show up here."
          : <>Following <Badge variant="secondary" className="mx-1 font-normal">{n}</Badge>{n === 1 ? "company" : "companies"} · {preview.active_count} with activity this week</>}
      </p>
    </Card>
  );
}
